import { PageBreadcrumbContainer } from "lib/page-breadcrumb-container"
import { HelpTag } from "routes/help/helpTag"
import { Box, Link } from "@mui/material"
import { useParams } from "react-router-dom"
import { noChange, useRefresh } from "lib/@hooks/useRefresh"
import { Bound } from "lib/@components/binding/Bound"
import { assetRegister } from "routes/asset-register/controller/asset-register"
import { navigate } from "lib/routes/navigate"
import { Feature } from "lib/@components/feature"
import { registerTab, Tabs } from "lib/@components/tabs"
import { Watcher } from "lib/system/server-events/use-watch"
import { FEATURE_FLAG_ASSET_MAPPING } from "routes/asset-register/feature-flag-asset-mapping"
import { HELPTAG_ASSET_REGISTER } from "routes/help/help-tag-constants"
import { emptyArray } from "lib/empty-array"
import { ExportRegisterButton } from "./asset-register-buttons/export-register-button"

import { ListItemBox } from "lib/@components/ListItemBox"
import { TopBox } from "lib/@components/topBox"
import { createPortalPair } from "lib/portals"
import { TreeToolbar } from "slot-definitions"
import { AssetList } from "./asset-list"
import { CurrentMappingStatus } from "routes/asset-register/plugins/asset-mapping/current-mapping-status"
import { htmlToText } from "lib/html-to-text"
import { getAssetCount } from "routes/asset-register/controller/get-asset-count"
import { TruncatedTypography } from "lib/@components/truncated-typography"

export const ASSET_TAB_ID = "asset-tabs"

export const [RegisterTitlePortal, RegisterTitleClient] = createPortalPair("register-title")

export const assetSortOptions = [
    { value: { sort: "description", descending: false }, label: "Description" },
    { value: { sort: "description", descending: true }, label: "Description (desc)" },
    { value: { sort: "tag", descending: false }, label: "Tag" },
    { value: { sort: "tag", descending: true }, label: "Tag (desc)" },
    { value: { sort: "type", descending: false }, label: "Type" },
    { value: { sort: "type", descending: true }, label: "Type (desc)" },
    { value: { sort: "category", descending: false }, label: "Category" },
    { value: { sort: "category", descending: true }, label: "Category (desc)" },
    { value: { sort: "space", descending: false }, label: "Space" },
    { value: { sort: "space", descending: true }, label: "Space (desc)" },
    { value: { sort: "system", descending: false }, label: "System" },
    { value: { sort: "system", descending: true }, label: "System (desc)" },
    { value: { sort: "manufacturer", descending: false }, label: "Manufacturer" },
    { value: { sort: "manufacturer", descending: true }, label: "Manufacturer (desc)" },
]

export function AssetRegister() {
    const { id } = useParams()
    const [register, refreshRegister] = assetRegister.useResultsOnce.withRefetch(id) ?? emptyArray
    const refresh = useRefresh(noChange)
    const assetCount = getAssetCount.useResults(id)
    const hasAssets = assetCount === undefined || Number(assetCount) > 0

    TreeToolbar.usePlug(<ExportRegisterButton id={id} />, [id])

    if (!register) {
        return null
    }

    return (
        <Watcher id={register}>
            <Bound
                refresh={refresh}
                target={register}
                register={register}
                refreshRegister={refreshRegister}
                hasAssets={hasAssets}
            >
                <PageBreadcrumbContainer
                    tightWidth="xl"
                    heading={
                        <ListItemBox sx={{ minWidth: 0 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <TruncatedTypography
                                    noWrap
                                    variant="inherit"
                                    component="span"
                                    data-cy="asset-register-heading-name"
                                    sx={{ display: "block", maxWidth: "100%", fontWeight: "inherit" }}
                                >
                                    {register?.name}
                                </TruncatedTypography>
                            </Box>

                            <Box position="relative" sx={{ flex: "0 0 auto", minWidth: 0, pl: 4 }}>
                                <RegisterTitlePortal />
                            </Box>
                        </ListItemBox>
                    }
                    parentCrumbs={
                        <Link sx={{ cursor: "pointer" }} onClick={go}>
                            Asset Registers
                        </Link>
                    }
                    title={register?.name}
                    sx={{ height: 1 }}
                    description={htmlToText(register?.description).trim()}
                    descriptionProps={{ sx: { color: "text.secondary", wordWrap: "break-word" } }}
                >
                    <HelpTag tags={[HELPTAG_ASSET_REGISTER]} />
                    <Feature feature={FEATURE_FLAG_ASSET_MAPPING}>
                        <CurrentMappingStatus />
                        <Tabs
                            id={ASSET_TAB_ID}
                            tabHolder={<TopBox />}
                            panelHolder={<Box flex={1} />}
                            searchParam="registerTab"
                        />
                    </Feature>
                </PageBreadcrumbContainer>
            </Bound>
        </Watcher>
    )

    function go() {
        navigate("/app/assets")
    }
}

registerTab({
    tab: ASSET_TAB_ID,
    id: "asset list",
    title: "Assets",
    content: <AssetList $lazy priority={10} />,
})
